import { useChatStreamMutation } from '@/api/piloteer';
import PiloteerLogo from '@/assets/logo.svg?react';
import { ChatMessage } from '@/types/chat';
import { Button } from '@/ui/@components/primitives/button';
import { ScrollArea } from '@/ui/@components/primitives/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/ui/@components/primitives/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/ui/@components/primitives/tooltip';
import { cn } from '@/utils/tailwind';
import {
  BarChart,
  Calendar,
  LucideIcon,
  RotateCcw,
  Send,
  Settings,
  Square,
  Users,
} from 'lucide-react';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MAX_INPUT_CHARS = 500;

type DisplayMessage = ChatMessage & {
  id: string;
  isLoading?: boolean;
};

const SUGGESTIONS: { icon: LucideIcon; text: string }[] = [
  {
    text: 'What patterns show up in my leadership scores?',
    icon: BarChart,
  },
  {
    text: "Where am I limiting my team's performance?",
    icon: Users,
  },
  {
    text: 'What changed last week and why did it matter?',
    icon: Calendar,
  },
  {
    text: 'What should I adjust before my next leadership meeting?',
    icon: Settings,
  },
];

export const ChatBubble: FunctionComponent = () => {
  const storageKey = 'piloteer-chat-state';
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const shouldFocusAfterStreamRef = useRef(false);

  const [chat, { isLoading }] = useChatStreamMutation();

  useEffect(() => {
    if (!isLoading && shouldFocusAfterStreamRef.current) {
      shouldFocusAfterStreamRef.current = false;
      inputRef.current?.focus();
    }
  }, [isLoading]);

  // Restore persisted chat state
  useEffect(() => {
    try {
      const rawState = sessionStorage.getItem(storageKey);
      if (!rawState) return;
      const parsed = JSON.parse(rawState) as {
        isOpen: boolean;
        inputValue: string;
        messages: Array<ChatMessage & { id: string }>;
      };
      setIsOpen(parsed.isOpen);
      setInputValue(parsed.inputValue ?? '');
      setMessages(
        (parsed.messages ?? []).map((message) => ({
          ...message,
          isLoading: false,
        }))
      );
    } catch {
      sessionStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  // Persist chat state per browser tab
  useEffect(() => {
    try {
      const stateToPersist = {
        isOpen,
        inputValue,
        messages: messages.map(({ id, role, content }) => ({
          id,
          role,
          content,
        })),
      };
      sessionStorage.setItem(storageKey, JSON.stringify(stateToPersist));
    } catch {
      // Ignore storage failures (private mode, quota, etc.)
    }
  }, [isOpen, inputValue, messages, storageKey]);

  // Auto-scroll to bottom when messages change. Instant scroll avoids competing
  // animations while token deltas arrive.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    const message = inputValue.trim();
    if (!message || isLoading) return;

    const userMessageId = crypto.randomUUID();
    const assistantMessageId = crypto.randomUUID();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Add user message
    const userMessage: DisplayMessage = {
      id: userMessageId,
      role: 'user',
      content: message,
    };

    // Add loading assistant message
    const loadingMessage: DisplayMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      isLoading: true,
    };

    setMessages((prev) => [...prev, userMessage, loadingMessage]);
    setInputValue('');
    if (inputRef.current) inputRef.current.style.height = 'auto';

    try {
      const conversationHistory: ChatMessage[] = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      await chat({
        message,
        conversationHistory,
        signal: controller.signal,
        onChunk: (chunk) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? { ...m, content: m.content + chunk }
                : m
            )
          );
        },
        onReplace: (content) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId ? { ...m, content } : m
            )
          );
        },
      }).unwrap();

      // Mark as finished loading
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId ? { ...m, isLoading: false } : m
        )
      );
      shouldFocusAfterStreamRef.current = true;
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('Failed to respond to chat: ', error);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId
            ? {
                ...m,
                content: 'Sorry, I encountered an error. Please try again.',
                isLoading: false,
              }
            : m
        )
      );
      shouldFocusAfterStreamRef.current = true;
    } finally {
      abortControllerRef.current = null;
    }
  }, [inputValue, isLoading, messages, chat]);

  const handleAbort = useCallback(() => {
    if (!abortControllerRef.current) return;
    abortControllerRef.current.abort();
    abortControllerRef.current = null;

    setMessages((prev) => {
      const lastAssistant = [...prev]
        .reverse()
        .find((m) => m.role === 'assistant' && m.isLoading);
      if (!lastAssistant) return prev;
      return prev.map((m) =>
        m.id === lastAssistant.id
          ? {
              ...m,
              content: m.content
                ? m.content + '\n\n*— Response stopped*'
                : '*Response stopped by user.*',
              isLoading: false,
            }
          : m
      );
    });
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  const handleClearChat = () => {
    setMessages([]);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <SheetTrigger asChild>
              <Button
                variant='outline'
                size='icon'
                className='rounded-full w-full px-4 '
              >
                <PiloteerLogo className='h-5 w-5' />
                Ask Piloteer
                <span className='sr-only'>Open Piloteer Intelligence</span>
              </Button>
            </SheetTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Piloteer Intelligence</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <SheetContent
        className='flex flex-col w-full sm:w-[45vw] sm:max-w-[45vw] p-0'
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <SheetHeader className='px-4 py-3 border-b pr-12'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <div className='h-8 w-8 rounded-full flex items-center justify-center'>
                <PiloteerLogo className='h-8 w-8' />
              </div>
              <div className='flex flex-col items-start'>
                <SheetTitle className='text-base'>
                  Piloteer Intelligence
                </SheetTitle>
                <p className='text-xs text-muted-foreground'>
                  Live insights from your real leadership data.
                </p>
              </div>
            </div>
            {messages.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={handleClearChat}
                      className='h-8 w-8 text-muted-foreground hover:text-foreground'
                    >
                      <RotateCcw className='h-4 w-4' />
                      <span className='sr-only'>New chat</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>New chat</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className='flex-1 px-4'>
          <div className='py-4 space-y-4'>
            {messages.length === 0 ? (
              <EmptyState onSuggestionClick={handleSuggestionClick} />
            ) : (
              messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))
            )}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        <div className='p-4 border-t bg-background'>
          <div className='flex flex-col gap-2'>
            <div className='flex items-end gap-2'>
              <textarea
                ref={inputRef}
                value={inputValue}
                rows={1}
                maxLength={MAX_INPUT_CHARS}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                }}
                onKeyDown={handleKeyDown}
                placeholder='Ask a question about your leadership performance...'
                disabled={isLoading}
                className='flex-1 resize-none overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
              />
              {isLoading ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleAbort}
                        size='icon'
                        variant='destructive'
                        className='shrink-0'
                      >
                        <Square className='h-3.5 w-3.5 fill-current' />
                        <span className='sr-only'>Stop generating</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Stop generating</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  onClick={handleSend}
                  disabled={
                    !inputValue.trim() || inputValue.length >= MAX_INPUT_CHARS
                  }
                  size='icon'
                  className='shrink-0'
                >
                  <Send className='h-4 w-4' />
                  <span className='sr-only'>Send message</span>
                </Button>
              )}
            </div>
            {inputValue.length > MAX_INPUT_CHARS * 0.8 && (
              <p
                className={cn(
                  'text-xs text-right',
                  inputValue.length >= MAX_INPUT_CHARS
                    ? 'text-destructive'
                    : 'text-muted-foreground'
                )}
              >
                {inputValue.length}/{MAX_INPUT_CHARS}
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const EmptyState: FunctionComponent<{
  onSuggestionClick: (question: string) => void;
}> = ({ onSuggestionClick }) => {
  return (
    <div className='flex flex-col justify-center py-8'>
      <h1 className='text-4xl font-semibold mb-1 max-w-[80%]'>
        What do you want to understand about your performance?
      </h1>
      <p className='text-sm text-muted-foreground mt-2 mb-8'>
        Piloteer analyzes your real meeting data to surface the behaviors
        driving performance.
      </p>
      <div className='w-full space-y-2'>
        <p className='text-xs text-muted-foreground font-medium mb-2'>
          Try asking:
        </p>
        {SUGGESTIONS.map(({ icon: Icon, text }, index) => (
          <button
            key={index}
            onClick={() => onSuggestionClick(text)}
            className='w-full text-left px-3 py-2 text-sm rounded-sm border border-border bg-muted/40 hover:bg-muted transition-colors cursor-pointer'
          >
            <div className='flex items-center gap-2'>
              <Icon className='h-4 w-4 text-muted-foreground shrink-0' />
              <span>{text}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const MessageBubble: FunctionComponent<{ message: DisplayMessage }> = ({
  message,
}) => {
  const isUser = message.role === 'user';

  return (
    <div
      className={cn(
        'flex gap-2 ',
        isUser ? 'justify-end' : 'justify-start',
        !message.content ? 'items-center' : ''
      )}
    >
      {!isUser && (
        <div className='h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5'>
          <PiloteerLogo className='h-3.5 w-3.5' />
        </div>
      )}
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-2.5 text-sm',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-muted rounded-bl-md'
        )}
      >
        {message.isLoading && !message.content ? (
          <div className='flex items-center gap-1.5 py-1 px-1'>
            <div className='h-2 w-2 rounded-full bg-current animate-typing-dot' />
            <div className='h-2 w-2 rounded-full bg-current animate-typing-dot [animation-delay:0.2s]' />
            <div className='h-2 w-2 rounded-full bg-current animate-typing-dot [animation-delay:0.4s]' />
          </div>
        ) : (
          <>
            {isUser ? (
              <p className='whitespace-pre-wrap break-words'>
                {message.content}
              </p>
            ) : (
              <div className='markdown-content space-y-2 text-sm'>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => (
                      <p className='break-words leading-relaxed'>{children}</p>
                    ),
                    h1: ({ children }) => (
                      <h1 className='text-base mt-3 mb-1 first:mt-0 break-words'>
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className='text-sm mt-3 mb-1 first:mt-0 break-words'>
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className='text-sm mt-2 mb-0.5 first:mt-0 break-words'>
                        {children}
                      </h3>
                    ),
                    li: ({ children }) => (
                      <li className='break-words leading-relaxed'>
                        {children}
                      </li>
                    ),
                    hr: () => <hr className='border-foreground/10 my-2' />,
                    a: ({ children, href }) => (
                      <a
                        href={href}
                        className='underline underline-offset-4 hover:text-foreground/70 transition-colors'
                        target='_blank'
                        rel='noreferrer'
                      >
                        {children}
                      </a>
                    ),
                    code: ({ children }) => (
                      <code className='rounded bg-foreground/10 px-1 py-0.5 text-[0.85em] font-mono'>
                        {children}
                      </code>
                    ),
                    pre: ({ children }) => (
                      <pre className='overflow-x-auto rounded-md bg-foreground/10 p-3 text-[0.85em] leading-relaxed font-mono my-1'>
                        {children}
                      </pre>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className='border-l-2 border-foreground/20 pl-3 italic text-muted-foreground my-1'>
                        {children}
                      </blockquote>
                    ),
                    table: ({ children }) => (
                      <div className='overflow-x-auto my-2'>
                        <table className='w-full text-xs border-collapse'>
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className='bg-foreground/5'>{children}</thead>
                    ),
                    tr: ({ children }) => (
                      <tr className='border-b border-foreground/10'>
                        {children}
                      </tr>
                    ),
                    th: ({ children }) => (
                      <th className='px-2 py-1.5 text-left font-semibold whitespace-nowrap'>
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className='px-2 py-1.5 break-words'>{children}</td>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
                {message.isLoading && (
                  <div className='flex items-center gap-1 mt-2'>
                    <div className='h-1 w-1 rounded-full bg-foreground/30 animate-pulse' />
                    <div className='h-1 w-1 rounded-full bg-foreground/30 animate-pulse [animation-delay:0.2s]' />
                    <div className='h-1 w-1 rounded-full bg-foreground/30 animate-pulse [animation-delay:0.4s]' />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
